import { cn } from "@client/utils/cn";
import { Button } from "@sfc/ui";
import type { MouseEvent, ReactNode } from "react";

// The quiet treatment shared by every action at the end of a round row
// (Proposals, Review, Download). It lives in one component because the styling
// carries two constraints that are easy to break from a call site:
//
// 1. Every color is scoped to `not-disabled:`. Button computes
//    `text-transparent` from `isLoading` so its spinner stands in for the
//    label, and an unscoped `text-muted-foreground` here outranks that in
//    tailwind-merge — which drops `text-transparent` from the class list
//    entirely and leaves the label sitting under the spinner. The row-hover
//    color needs the same scoping for the same reason: `group-hover:` beats an
//    unmodified `text-transparent` on specificity, so without it the label
//    reappears whenever the row is hovered mid-download. Button sets
//    `disabled` whenever `isLoading` is set, so scoping both colors to the
//    enabled state keeps them mutually exclusive with the loading state by
//    construction rather than by cascade order.
// 2. `size="sm"` is 32px tall, under the 44px touch minimum in DESIGN.md. This
//    list is the funder iPad's landing screen, so coarse pointers get a full
//    44px hit area (and a little more room between neighbors) while mouse
//    users keep the compact row.
export const ROUND_ACTION_CLASS_NAME = cn(
  "border border-transparent",
  "not-disabled:text-muted-foreground group-hover:not-disabled:text-foreground",
  "hover:border-border hover:bg-surface-raised hover:shadow-raised-soft",
  "pointer-coarse:min-h-11",
);

type RoundActionButtonProps = { children: ReactNode } & (
  | { href: string; isLoading?: never; onClick?: never }
  | {
      href?: never;
      isLoading?: boolean;
      onClick: (event: MouseEvent) => void;
    }
);

export function RoundActionButton({
  href,
  isLoading,
  onClick,
  children,
}: RoundActionButtonProps) {
  if (href != null) {
    return (
      <Button
        variant="nav"
        size="sm"
        className={ROUND_ACTION_CLASS_NAME}
        nativeButton={false}
        render={<a href={href} />}
      >
        {children}
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant="nav"
      size="sm"
      className={ROUND_ACTION_CLASS_NAME}
      isLoading={isLoading}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}
